  import React, { useEffect, useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { apiCall } from "utils/apiClient";
import Pagination from "components/pagination";
import WithdrawalRequestsTab from "./components/WithdrawalRequestsTab";

const PartnerWithdrawalRequests = () => {
  // State Management
  const [allRequests, setAllRequests] = useState([]);
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [partnerRequests, setPartnerRequests] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // ========== API 1: Fetch All Withdrawal Requests ==========
  const fetchAllRequests = async () => {
    setLoading(true);
    try {
      console.log("📍 Calling API: admin/wallet/withdraw-requests");
      
      const result = await apiCall({
        endpoint: "admin/wallet/withdraw-requests",
        method: "GET",
      });

      console.log("✅ API Response:", result);

      // Extract data properly
      let requestsData = [];
      
      if (result?.data) {
        if (Array.isArray(result.data)) {
          requestsData = result.data;
        } else if (result.data.data && Array.isArray(result.data.data)) {
          requestsData = result.data.data;
        } else if (result.data.requests && Array.isArray(result.data.requests)) {
          requestsData = result.data.requests;
        }
      }

      console.log("📊 Extracted Requests:", requestsData);

      setAllRequests(requestsData);

      // Extract unique partners from requests
      const partnersMap = new Map();
      
      requestsData.forEach((request) => {
        const pId = request.partner_id || request.partnerId;
        const pName = request.partner_name || request.partnername || request.name || `Partner ${pId}`;

        if (pId && !partnersMap.has(pId)) {
          partnersMap.set(pId, {
            id: pId,
            name: pName,
          });
        }
      });

      const partnersList = Array.from(partnersMap.values());
      console.log("👥 Partners Found:", partnersList);

      setPartners(partnersList);

      if (partnersList.length === 0) {
        toast.warning("⚠️ No partners found in withdrawal requests");
      } else {
        toast.success(`✅ Found ${partnersList.length} partners with ${requestsData.length} requests`);
      }
    } catch (error) {
      console.error("❌ Error fetching requests:", error);
      toast.error("Failed to fetch withdrawal requests");
    } finally {
      setLoading(false);
    }
  };

  // ========== API 2: Fetch Partner-Specific Requests ==========
  const fetchPartnerRequests = async (partnerId) => {
    if (!partnerId) {
      setClear();
      return;
    }

    setLoading(true);
    try {
      console.log(`📍 Calling API: admin/wallet/withdraw-requests/${partnerId}`);

      const result = await apiCall({
        endpoint: `admin/wallet/withdraw-requests/${partnerId}`,
        method: "GET",
      });

      console.log("✅ Partner API Response:", result);

      // Extract data properly
      let requestsData = [];

      if (result?.data) {
        if (Array.isArray(result.data)) {
          requestsData = result.data;
        } else if (result.data.data && Array.isArray(result.data.data)) {
          requestsData = result.data.data;
        } else if (result.data.requests && Array.isArray(result.data.requests)) {
          requestsData = result.data.requests;
        }
      }

      console.log(`📋 Requests for Partner ${partnerId}:`, requestsData);

      setPartnerRequests(requestsData);
      setCurrentPage(1);

      toast.success(`✅ Loaded ${requestsData.length} requests for this partner`);
    } catch (error) {
      console.error("❌ Error fetching partner requests:", error);
      toast.error("Failed to fetch partner requests");
      setPartnerRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const setClear = () => {
    setPartnerRequests([]);
    setCurrentPage(1);
  };

  // ========== Effects ==========
  useEffect(() => {
    fetchAllRequests();
  }, []);

  useEffect(() => {
    if (selectedPartnerId) {
      fetchPartnerRequests(selectedPartnerId);
    } else {
      setClear();
    }
  }, [selectedPartnerId]);

  // ========== Filtering & Pagination ==========
  const filteredRequests = partnerRequests.filter((req) =>
    (req.reference || req.reference_no || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (req.method || req.payment_method || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedRequests = filteredRequests.slice(startIndex, startIndex + recordsPerPage);
  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / recordsPerPage));

  // ========== Actions ==========
  const handleApprove = async (requestId) => {
    try {
      console.log(`Approving request: ${requestId}`);
      const result = await apiCall({
        endpoint: `admin/wallet/withdraw-requests/${requestId}/approve`,
        method: "POST",
      });

      if (result?.success || result?.status === 200) {
        toast.success("✅ Request approved successfully");
        if (selectedPartnerId) {
          fetchPartnerRequests(selectedPartnerId);
        }
      }
    } catch (error) {
      console.error("❌ Approve error:", error);
      toast.error("Failed to approve request");
    }
  };

  const handleReject = async (requestId) => {
    try {
      console.log(`Rejecting request: ${requestId}`);
      const result = await apiCall({
        endpoint: `admin/wallet/withdraw-requests/${requestId}/reject`,
        method: "POST",
      });

      if (result?.success || result?.status === 200) {
        toast.success("✅ Request rejected successfully");
        if (selectedPartnerId) {
          fetchPartnerRequests(selectedPartnerId);
        }
      }
    } catch (error) {
      console.error("❌ Reject error:", error);
      toast.error("Failed to reject request");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" reverseOrder={false} />

      <div className="mt-6 p-6 bg-white rounded-lg shadow w-full space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Withdrawal Requests</h2>
          <p className="text-sm text-gray-600 mt-1">
            Partners: {partners.length} | Total Requests: {allRequests.length} | Page: {currentPage}/{totalPages}
          </p>
        </div>

        {/* Empty State - No Requests */}
        {allRequests.length === 0 ? (
          <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Withdrawal Requests Found</h3>
            <p className="text-gray-600 mb-2">There are currently no withdrawal requests in the system.</p>
            <p className="text-sm text-gray-500">Requests will appear here once partners submit withdrawal requests.</p>
          </div>
        ) : (
          <>
            {/* Partner Selector & Search */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Partner
                </label>
                {loading && partners.length === 0 ? (
                  <div className="p-3 bg-blue-50 border border-blue-300 rounded text-sm text-blue-700">
                    Loading partners...
                  </div>
                ) : partners.length === 0 ? (
                  <div className="p-3 bg-red-50 border border-red-300 rounded text-sm text-red-700">
                    ❌ No partners found. Check API response in console.
                  </div>
                ) : (
                  <select
                    value={selectedPartnerId || ""}
                    onChange={(e) => setSelectedPartnerId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">-- Select a Partner --</option>
                    {partners.map((partner) => (
                      <option key={partner.id} value={partner.id}>
                        {partner.name} (ID: {partner.id})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  placeholder="Search by reference or method..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Main Content */}
            {!selectedPartnerId ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-lg text-gray-500">👆 Please select a partner to view withdrawal requests</p>
              </div>
            ) : loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Loading requests...</p>
              </div>
            ) : paginatedRequests.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-600">No withdrawal requests found for this partner.</p>
              </div>
            ) : (
              <>
                <WithdrawalRequestsTab
                  requests={paginatedRequests}
                  loading={loading}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  startIndex={startIndex}
                />

                <div className="mt-4">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    recordsPerPage={recordsPerPage}
                    onRecordsPerPageChange={(val) => {
                      setRecordsPerPage(val);
                      setCurrentPage(1);
                    }}
                    totalRecords={filteredRequests.length}
                    count={paginatedRequests.length}
                  showFirstLastControls
                  compact
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PartnerWithdrawalRequests;
